import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ConfirmationOrder } from '@models/confirmation-order';
import {
  DayTradesViewModel,
  IDayTradesOrders,
  IPotencialDayTradesDetail,
} from '@models/day-trades.class';
import { MARKET_STATUS_TYPE } from '@models/market.class';
import { TGlobalOrder } from '@models/orders.class';
import { CAPITAL_OPERATIONS } from '@models/puppet.class';
import { GENERAL_CODE_CALLBACKS, TradeInfo } from '@models/trade-info.class';
import {
  TRADING_USA_ORDER_STATUS,
  TRADING_ORDER_WAYS,
} from '@models/trades.class';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { AppState, TRACKING_EVENTS } from 'src/app';
import { DW_ACCOUNT_STATUS_TYPE } from 'src/app/app.constants';
import { selectTotals } from 'src/app/state/selectors/current-contract.selectors';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { SideModalComponent } from 'src/components/side-modal/side-modal.component';
import {
  AnalyticsTrackingService,
  TrackingOrderType,
} from 'src/core/services/analyticsTracking.service';
import { PuppetService } from 'src/core/services/puppet.service';

@Component({
  selector: 'hb-usa-order',
  templateUrl: './usa.component.html',
  styleUrls: ['./usa.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UsaOrderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public operation: CAPITAL_OPERATIONS;
  @ViewChild('doubleSellingModal')
  doubleSellingModal: SideModalComponent;
  @ViewChild('sideModal') sideModal: SideModalComponent;

  public readonly capitalOperations = CAPITAL_OPERATIONS;

  public unitPrice = 0;
  public positionShares = 0;
  public issueOperationStatus: MARKET_STATUS_TYPE;
  public marketStatus = MARKET_STATUS_TYPE;

  public actionCopies: {
    noun: { [key in CAPITAL_OPERATIONS]: string };
    verb: { [key in CAPITAL_OPERATIONS]: string };
    pluralVersus: { [key in CAPITAL_OPERATIONS]: string };
    plural: { [key in CAPITAL_OPERATIONS]: string };
  } = {
    noun: {
      buy: 'compra',
      sell: 'venta',
    },
    verb: {
      buy: 'comprar',
      sell: 'vender',
    },
    pluralVersus: {
      sell: 'compras',
      buy: 'ventas',
    },
    plural: {
      sell: 'ventas',
      buy: 'compras',
    },
  };

  public minimumToOperate = 20;
  public amountGroupValues: Array<GroupValue<number>> = [
    { value: 100 },
    { value: 500 },
    { value: 1000 },
    { value: 0, label: 'Máx' },
  ];

  public amountToOperate: number;

  public availableToBuy = 0;
  public availableToSell = 0;
  public disableForm = true;
  public enableTradingUsaOperation = true;
  public showLiquidateOnlyMessage = false;

  public confirmationOrder: ConfirmationOrder = {} as ConfirmationOrder;
  public showConfirmation: boolean;

  public isOutOfOperationTime = false;

  public previousMax: GroupValue<number>;
  public show3DayTrade: boolean;
  public readonly MAX_DAY_TRADES: number = 3;
  public showMaxDayTradesMessage: boolean;
  public showThirdVirtualDt: boolean;
  public showDoubleSellMessage = false;

  public dayTrades: IDayTradesOrders[];
  public showTooltip = false;
  public readonly DAY_TRADE_INFO: string =
    'Si la orden se completa habrás alcanzado el límite, de lo contrario podrás realizar otro day trade';
  public readonly VIRTUAL_DAY_TRADE_INFO: string =
    'Tienes una orden en proceso que puede generar tu <strong>tercer day trade.</strong> Si se completa antes de que cierre el mercado alcanzarás tu límite, en caso contrario podrás realizar otro';
  public nextDateLabel: string;
  public showBpSecureAmount = false;
  public showDayTradesInfo = false;
  public showModalLiquidateOnly = false;
  public prevPendingOrder: TGlobalOrder;

  private destroyed$ = new Subject<void>();

  private liquidateOnlyStatus: string;
  private tradeInfo: TradeInfo;
  private lastPrice: number;
  private potencialDayTrades: IPotencialDayTradesDetail[] =
    new Array<IPotencialDayTradesDetail>();
  private pendingOrders: Array<TGlobalOrder>;

  private subscriptions = new Subscription();

  constructor(
    public readonly puppetService: PuppetService,
    private readonly analyticsTrackingService: AnalyticsTrackingService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.subscribeToBuyingPower();
    this.subscribeToIssueData();
    this.subscribeToMarketTime();
    this.scanForTradingUsaOperation();
    this.scanForLiquidateOnlyStatus();
    this.listenerDayTrades();
    this.listernerPendingOrders();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.destroyed$.next();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.operation) {
      this.adjustMaxBasedOnOperation(changes.operation.currentValue);
      this.validateShowLiquidateMessage();

      this.confirmationOrder.instrumentId &&
        this.potencialDayTrades.length &&
        this.validatePotencialDayTrades();
      this.confirmationOrder.instrumentId && this.validateVirtualDt();
      this.enableOrDisableForm();
      this.validateDoubleSelling();
    }
  }

  public handleOperateClick() {
    this.confirmationOrder = {
      ...this.confirmationOrder,
      titles: this.amountToOperate / this.unitPrice,
      isBuy: this.operation === CAPITAL_OPERATIONS.buy,
      total:
        this.operation === CAPITAL_OPERATIONS.buy &&
        // This will use the full buyingPower when the input value is close
        // enough to it
        Math.abs(this.availableToBuy - this.amountToOperate) < 0.01
          ? this.availableToBuy
          : this.amountToOperate,
      type: 'A mercado',
      sellAllPosition:
        this.operation === CAPITAL_OPERATIONS.sell &&
        // Prefer to check the difference against a small number, to prevent getting
        // false negatives due to floating point arithmetic.
        // This also takes in consideration that the availableToSell has up to 8 digits,
        // but the total has only 2 when you type it manually.
        Math.abs(this.availableToSell - this.amountToOperate) < 0.01,
    };

    this.showConfirmation = true;
  }

  public registerOrder() {
    this.tradeInfo = new TradeInfo(
      true,
      this.confirmationOrder,
      TrackingOrderType.Market,
      window.location.pathname,
      (this.amountToOperate / this.unitPrice).toString(),
      this.operation === CAPITAL_OPERATIONS.buy ? 'compra' : 'venta',
      `${this.amountToOperate}`,
      `${this.unitPrice}`,
      undefined,
      undefined,
      this.amountToOperate === this.previousMax.value,
    );

    this.puppetService.placeOrder$(this.confirmationOrder).subscribe({
      next: () => {
        this.showConfirmation = false;

        this.tradeInfo.setSuccess(true);
        this.tradeInfo.setCallback(GENERAL_CODE_CALLBACKS.SUCCESS);
        const usaTradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          usaTradeInfoData,
        );

        this.resetForm();
      },
      error: ({ error }) => {
        this.showConfirmation = false;

        this.tradeInfo.setSuccess(false);
        this.tradeInfo.setCallback(error.message);
        const usaTradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          usaTradeInfoData,
        );
      },
    });
  }

  public handleAmountToOperateChange(value: string | number) {
    if (typeof value === 'string' || isNaN(value)) {
      this.amountToOperate = 0;
      return;
    }

    this.amountToOperate = value;
  }

  public cancelConfirmation() {
    this.showConfirmation = false;
  }

  public handleOpenDayTradeInfo() {
    this.showDayTradesInfo = true;
    this.sideModal.showModal();
  }

  public handleCloseDayTradeInfo() {
    this.showBpSecureAmount = false;
    this.showDayTradesInfo = false;
    this.sideModal.handleHide();
  }

  public handleBuyingPowerSecureAmount() {
    this.showBpSecureAmount = true;
    this.sideModal.showModal();
  }

  public handleToggleModal(): void {
    this.showModalLiquidateOnly = !this.showModalLiquidateOnly;
    this.showModalLiquidateOnly && this.addLiquidateOnlyEvent();
  }

  private addLiquidateOnlyEvent(): void {
    this.analyticsTrackingService.logEvent(
      TRACKING_EVENTS.trade_blocked_liquidate_only.event,
      {
        ...TRACKING_EVENTS.trade_blocked_liquidate_only.success,
        Origin: this.analyticsTrackingService.processOrigin(
          window.location.pathname,
        ),
      },
    );
  }
  public handleOpenDoubleSellingModal() {
    this.doubleSellingModal.showModal();
  }
  public handleCloseDoubleSellingModal(isCanceled?: boolean) {
    this.doubleSellingModal.handleHide();
  }

  /**
   * Update the maximum value to operate and preserve it when switching BUY/SELL
   * @param operation active in the puppet
   */
  private adjustMaxBasedOnOperation(operation: CAPITAL_OPERATIONS) {
    this.previousMax =
      this.amountGroupValues[this.amountGroupValues.length - 1];

    const newMax =
      operation === CAPITAL_OPERATIONS.buy
        ? this.availableToBuy
        : this.availableToSell;

    // This adjust the input value when MAX was selected before
    // switching to a different CAPITAL_OPERATIONS
    if (this.amountToOperate === this.previousMax.value) {
      this.amountToOperate = newMax;
    }

    this.previousMax.value = newMax;
  }

  private enableOrDisableForm() {
    if (!this.lastPrice) {
      this.disableForm = true;
      this.resetForm();

      return;
    }

    if (this.showMaxDayTradesMessage) {
      this.disableForm = true;
      return;
    }

    if (this.showThirdVirtualDt) {
      this.disableForm = true;
      return;
    }

    if (
      this.showLiquidateOnlyMessage &&
      this.operation === CAPITAL_OPERATIONS.buy
    ) {
      this.disableForm = true;
      this.resetForm();
      return;
    }

    if (this.issueOperationStatus === MARKET_STATUS_TYPE.active) {
      this.disableForm = false;
      return;
    }

    if (this.issueOperationStatus === MARKET_STATUS_TYPE.closeOnly) {
      this.disableForm = this.operation === CAPITAL_OPERATIONS.buy;
    }

    if (
      this.issueOperationStatus === MARKET_STATUS_TYPE.halted ||
      this.issueOperationStatus === MARKET_STATUS_TYPE.inactive
    ) {
      this.disableForm = true;
    }

    if (this.disableForm) {
      this.resetForm();
    }
  }

  private subscribeToBuyingPower() {
    this.subscriptions.add(
      this.store
        .select(selectTotals)
        .pipe(filter((totals) => !!totals))
        .subscribe((totals) => {
          this.availableToBuy = totals.buyingPowerSecureAmount;
          this.adjustMaxBasedOnOperation(this.operation);
        }),
    );
  }

  private subscribeToIssueData() {
    combineLatest(
      this.puppetService.selectedIssue$,
      this.puppetService.position$,
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([
          { lastPrice, issueName, issueID, instrumentId, status },
          { shares, marketValue },
        ]) => {
          this.issueOperationStatus = status;
          this.confirmationOrder.issueName = issueName;
          this.confirmationOrder.issueID = issueID;
          this.confirmationOrder.instrumentId = instrumentId;

          if (issueID) {
            this.validatePotencialDayTrades();
            this.validateVirtualDt();
            this.validateDoubleSelling();
          }

          !issueName && this.cleanDayTradeInfo();

          // TODO: Discomment to make the schedule check valid
          this.lastPrice = lastPrice;

          this.enableOrDisableForm();

          this.unitPrice = lastPrice;
          this.positionShares = shares;
          this.availableToSell =
            marketValue !== undefined
              ? marketValue
              : lastPrice && shares
              ? lastPrice * shares
              : 0;
        },
      );
  }

  private cleanDayTradeInfo() {
    this.show3DayTrade = false;
    this.showMaxDayTradesMessage = false;
    this.showThirdVirtualDt = false;
  }

  private subscribeToMarketTime() {
    this.puppetService.marketTime$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((marketTime) => {
        const now = new Date().getTime();

        this.isOutOfOperationTime =
          new Date(marketTime.startTime).getTime() > now ||
          now > new Date(marketTime.endTime).getTime();
      });
  }

  private resetForm() {
    this.amountToOperate = undefined;
  }

  private scanForTradingUsaOperation() {
    this.store
      .select(selectFeatureFlag('tradingUsaOperation'))
      .pipe(
        filter(
          (enableTradingUsaOperation: any) =>
            enableTradingUsaOperation !== undefined,
        ),
      )
      .subscribe((tradingUsaOperation: any) => {
        this.enableTradingUsaOperation = tradingUsaOperation;
      });
  }

  private scanForLiquidateOnlyStatus() {
    this.subscriptions.add(
      this.store
        .select('dwAccountStatus')
        .pipe(filter((accountStatus?: string) => accountStatus !== undefined))
        .subscribe((accountStatus: string) => {
          this.liquidateOnlyStatus = accountStatus;
          this.validateShowLiquidateMessage();
        }),
    );
  }

  private listenerDayTrades() {
    this.subscriptions.add(
      this.store
        .select('dayTrades')
        .pipe(
          filter(
            (value: any) =>
              value !== undefined && value.isDayTradeValidationActive,
          ),
        )

        .subscribe((items: DayTradesViewModel) => {
          this.dayTrades = items.dayTrades;
          this.nextDateLabel = moment(items.nextAvailableDayTrade).format('LL');
          this.potencialDayTrades = items.potencialDayTrades;
          if (items.dayTrades.length && this.confirmationOrder.issueID) {
            this.validatePotencialDayTrades();
            this.validateVirtualDt();
          }
        }),
    );
  }

  private listernerPendingOrders() {
    this.subscriptions.add(
      this.store
        .select('blotter')
        .pipe(
          map((values: any) =>
            values.get('capitals') !== undefined
              ? (values.get('capitals').toJS() as Array<TGlobalOrder>)
              : ([] as Array<TGlobalOrder>),
          ),
          filter(
            (blotterOrders: Array<TGlobalOrder>) => blotterOrders.length > 0,
          ),
          distinctUntilChanged(),
        )
        .subscribe((blotterOrders: Array<TGlobalOrder>) => {
          this.pendingOrders = blotterOrders.filter(
            (order) =>
              order.status === TRADING_USA_ORDER_STATUS.pending &&
              order.way === TRADING_ORDER_WAYS.sell,
          );
        }),
    );
  }

  private validatePotencialDayTrades() {
    this.show3DayTrade = false;
    this.showMaxDayTradesMessage = false;
    if (this.dayTrades && this.dayTrades.length < 2) {
      return;
    }

    const foundIssue = this.potencialDayTrades.find((item) => {
      return (
        item.instrumentId === this.confirmationOrder.instrumentId &&
        item.side === this.operation
      );
    });

    if (foundIssue) {
      this.show3DayTrade = this.dayTrades.length <= this.MAX_DAY_TRADES;
      this.showMaxDayTradesMessage =
        this.getDaytradesCompleted() === this.MAX_DAY_TRADES;
    }
  }

  private validateVirtualDt() {
    this.showThirdVirtualDt = false;
    if (this.dayTrades && this.dayTrades.length === this.MAX_DAY_TRADES) {
      const virtualDt = this.dayTrades.find((item) => {
        return item.isVirtual;
      });

      const foundIssue = this.potencialDayTrades.find((item) => {
        return (
          item.instrumentId === this.confirmationOrder.instrumentId &&
          item.side === this.operation
        );
      });

      if (virtualDt && foundIssue) {
        this.showThirdVirtualDt = true;
      }
    }
  }

  private validateShowLiquidateMessage() {
    this.showLiquidateOnlyMessage =
      this.liquidateOnlyStatus === DW_ACCOUNT_STATUS_TYPE.open_no_new_trades &&
      this.operation === CAPITAL_OPERATIONS.buy;
  }

  private getDaytradesCompleted(): number {
    return this.dayTrades.filter((item) => {
      return !item.isVirtual;
    }).length;
  }

  private validateDoubleSelling(): void {
    if (this.operation === CAPITAL_OPERATIONS.buy) {
      this.showDoubleSellMessage = false;
      return;
    }

    this.prevPendingOrder =
      this.pendingOrders &&
      this.pendingOrders.find(
        (order) => order.issueId === this.confirmationOrder.issueID,
      );

    if (this.prevPendingOrder) {
      this.prevPendingOrder = {
        issueName: this.confirmationOrder.issueName,
        ...this.prevPendingOrder,
      };
    }

    this.showDoubleSellMessage = !!this.prevPendingOrder;
  }
}

interface GroupValue<T> {
  label?: string;
  value: T;
}
